import { useMutation, useQueryClient } from '@tanstack/react-query';
import Config from 'src/common/Config';

const API_URL = Config.getApiUrl();

type RevertResponse = {
  versionId: string;
  versionNumber: number;
  markdownContent: string;
  message: string;
};

export function useRevertToVersion(documentId?: string) {
  const queryClient = useQueryClient();

  const matchesVersionsQuery = (query: { queryKey: readonly unknown[] }) =>
    query.queryKey.some(
      key => typeof key === 'string' && key.includes(`documents/${documentId}/versions`),
    );

  const { mutateAsync, error, isLoading, reset } = useMutation({
    mutationFn: async (versionId: string): Promise<RevertResponse> => {
      if (!documentId) throw new Error('documentId is required');
      const response = await fetch(`${API_URL}/documents/${documentId}/revert-to-version`, {
        method: 'POST',
        body: JSON.stringify({ versionId }),
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (response.status < 200 || response.status > 299) throw response;
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: matchesVersionsQuery });
    },
  });

  return {
    revertToVersion: mutateAsync,
    error,
    isLoading,
    reset,
  };
}
